"use client";

import { useRouter } from "next/navigation";
import { formatDelta, formatMoney, formatPercent, formatPercentile, formatRatioPercent } from "@/lib/format";
import type { NicheRanking, NicheSortKey } from "@/lib/types";

type Props = {
  niches: NicheRanking[];
  sortBy: NicheSortKey;
  onSort: (key: NicheSortKey) => void;
};

const COLUMNS: { key: NicheSortKey | "rank" | "label" | "confidence" | "explanation"; header: string; sortable: boolean }[] = [
  { key: "rank", header: "#", sortable: false },
  { key: "label", header: "Niche", sortable: false },
  { key: "opportunityScore", header: "Score", sortable: true },
  { key: "gapScore", header: "Gap", sortable: true },
  { key: "delta", header: "7d Delta", sortable: true },
  { key: "medianMrr", header: "Median MRR", sortable: true },
  { key: "averageGrowth30d", header: "Avg Growth", sortable: true },
  { key: "startupCount", header: "Startups", sortable: true },
  { key: "forSaleRatio", header: "For Sale", sortable: true },
  { key: "explanation", header: "Driver", sortable: false },
  { key: "confidence", header: "Confidence", sortable: false }
];

function rankDisplay(index: number): string {
  return String(index + 1).padStart(2, "0");
}

export function NicheTable({ niches, sortBy, onSort }: Props) {
  const router = useRouter();

  return (
    <div className="niche-table-wrap">
      <table className="niche-table">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={col.sortable && sortBy === col.key ? "sorted" : ""}
                onClick={() => col.sortable && onSort(col.key as NicheSortKey)}
                title={col.key === "opportunityScore" ? "Weighted score using median MRR, growth, total MRR, crowding, and sale liquidity." : undefined}
              >
                {col.header}
                {col.sortable && sortBy === col.key ? " v" : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {niches.map((niche, index) => (
            <tr
              key={niche.category}
              className="niche-row"
              onClick={() => router.push(`/dashboard/${niche.category}`)}
            >
              <td>
                <span className={`rank-badge ${index < 3 ? "rank-medal" : "rank-num"}`}>
                  {rankDisplay(index)}
                </span>
              </td>
              <td className="cell-name">
                <div className="niche-row-main">
                  <span className="niche-link">{niche.label}</span>
                  <span className="niche-row-sub">
                    {niche.insights[0]?.text ?? `Top startup: ${niche.topStartup}`}
                  </span>
                </div>
              </td>
              <td className="cell-num">{niche.opportunityScore}</td>
              <td className="cell-num" title="Underserved market signal: demand × monetization ÷ startups">
                {niche.gapScore != null ? Math.round(niche.gapScore).toLocaleString() : "—"}
              </td>
              <td>
                <span className={niche.delta?.delta && niche.delta.delta > 0 ? "growth-positive" : niche.delta?.delta && niche.delta.delta < 0 ? "growth-negative" : "growth-neutral"}>
                  {formatDelta(niche.delta?.delta ?? null)}
                </span>
              </td>
              <td className="cell-money">{formatMoney(niche.medianMrr)}</td>
              <td>
                <span className={niche.averageGrowth30d && niche.averageGrowth30d > 0 ? "growth-positive" : niche.averageGrowth30d && niche.averageGrowth30d < 0 ? "growth-negative" : "growth-neutral"}>
                  {formatPercent(niche.averageGrowth30d)}
                </span>
              </td>
              <td className="cell-num">{niche.startupCount}</td>
              <td className="cell-num">{formatRatioPercent(niche.forSaleRatio)}</td>
              <td className="cell-name">
                <div className="niche-row-main">
                  <span className="niche-link">{niche.scoreExplanation.strongestFactor}</span>
                  <span className="niche-row-sub">{formatPercentile(niche.percentiles.opportunityScore)}</span>
                </div>
              </td>
              <td>
                <span className={`cell-badge cell-badge--${niche.confidenceBand}`}>
                  {niche.confidenceBand}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
